import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import styles from "../../styles/headerStyle.js";
import { useTranslation } from "react-i18next";
import { Box, useTheme, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toLocalizedPath } from "../../utils/i18nRoute"; // [I18N_ROUTE]
import { navigateWithIframeEscape } from "../../utils/navigationUtils";
import moment from "moment";
import { haptic } from "../../utils/haptics";
import { FONT_FAMILY } from "common/sharedFunctions.js";

const useStyles = makeStyles(styles);

// ✅ Robust scroll getter for Safari & modern browsers
const getScrollTop = () => {
  if (typeof window === "undefined") return 0;
  return (
    window.scrollY ||
    window.pageYOffset ||
    document.scrollingElement?.scrollTop ||
    document.documentElement?.scrollTop ||
    document.body?.scrollTop ||
    0
  );
};

export default function Header(props) {
  const { i18n, t } = useTranslation();
  const isRTL = i18n.dir();
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  const settings = useSelector((state) => state.settingsdata.settings);

  // forceSolid: if true, header is always opaque (Login, About, etc.)
  // Otherwise, it starts transparent and becomes opaque on scroll (Home)
  const { color, rightLinks, leftLinks, fixed, absolute, forceSolid } = props;

  const handleDrawerToggle = () => {
    haptic.light();
    setMobileOpen((v) => !v);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  React.useEffect(() => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // ✅ Single, robust scroll effect supporting Safari momentum & scrollend
  React.useEffect(() => {
    if (typeof window === "undefined") return;

    let rafId = null;
    let bounceTimeout = null;

    const update = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        // Safari momentum overscroll can yield negative values at top
        const y = Math.max(0, getScrollTop());
        const isScrolled = y > 10;
        setScrolled(isScrolled);
      });

      // Extra guarantee for Safari rubber-band deceleration at top of page
      clearTimeout(bounceTimeout);
      bounceTimeout = setTimeout(() => {
        const y = Math.max(0, getScrollTop());
        setScrolled(y > 10);
      }, 80);
    };

    // initial check
    update();

    // Listen on window
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("scrollend", update, { passive: true });

    // Also listen on scrollingElement / documentElement
    const se = document.scrollingElement || document.documentElement;
    if (se) {
      se.addEventListener("scroll", update, { passive: true });
      se.addEventListener("scrollend", update, { passive: true });
    }

    // Gestures
    window.addEventListener("wheel", update, { passive: true });
    window.addEventListener("touchmove", update, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(bounceTimeout);
      window.removeEventListener("scroll", update);
      window.removeEventListener("scrollend", update);
      if (se) {
        se.removeEventListener("scroll", update);
        se.removeEventListener("scrollend", update);
      }
      window.removeEventListener("wheel", update);
      window.removeEventListener("touchmove", update);
    };
  }, [forceSolid]);

  const appBarClasses = classNames({
    [classes.appBar]: true,
    [classes[color]]: color,
    [classes.absolute]: absolute,
    [classes.fixed]: fixed,
  });

  const isDarkMode = theme.palette.mode === "dark";
  // ✅ Dark mode = ALWAYS white logo. Light mode = black logo only if scrolled/opaque.
  const useBlackLogo = !isDarkMode && (forceSolid || scrolled);

  const brandComponent = (
    <Button href={toLocalizedPath("/", window.location.pathname)} className={classes.title}>
      {useBlackLogo ? (
        <img
          src={require("../../assets/img/logo138x75black.png")}
          alt={t("blacklogo", "blackLogo")}
          style={{ width: "auto", height: "45px", objectFit: "contain" }}
        />
      ) : (
        <img
          src={require("../../assets/img/logo138x75white.png")}
          alt={t("whitelogo", "whiteLogo")}
          style={{ width: "auto", height: "45px", objectFit: "contain" }}
        />
      )}
    </Button>
  );
  const hamburgerColor =
    theme.palette.mode === "dark"
      ? "#fff"
      : scrolled
        ? "rgba(0,0,0,0.78)"
        : "#fff";
  const isOpaque = forceSolid || scrolled;

  const navigate = useNavigate();
  const currentActiveLang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";

  const switchLanguage = (targetLang) => {
    try {
      if (currentActiveLang === targetLang) return;
      haptic.selection();

      const dateLocale = targetLang === "en" ? "en-gb" : "fr";
      i18n.changeLanguage(targetLang);
      moment.locale(dateLocale);
      localStorage.setItem("lang", JSON.stringify({ langLocale: targetLang, dateLocale }));

      const currentPath = location.pathname + location.search + location.hash;
      const target = toLocalizedPath(currentPath, targetLang);

      navigateWithIframeEscape(target, navigate);
    } catch (e) {
      console.error("[Header] Language switch error:", e);
    }
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={(theme) => ({
        top: 0,
        left: 0,
        right: 0,
        margin: "0 !important",
        paddingTop: "env(safe-area-inset-top, 0px)",
        zIndex: theme.zIndex.drawer + 10,
        // ✅ Safari WebKit GPU compositor pinning: prevents detachment / disappearance during fast momentum scroll
        WebkitTransform: "translate3d(0, 0, 0)",
        transform: "translate3d(0, 0, 0)",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        // ✅ Animate background/border only (NEVER backdrop-filter in transition/willChange on Safari)
        willChange: "background-color, border-color",
        transition: "background-color 200ms ease, border-color 200ms ease",
        // Using high specificity selectors to override JSS classes
        "&.MuiPaper-root, &.MuiAppBar-root": {
          backgroundColor: isOpaque
            ? theme.palette.mode === "dark"
              ? "rgba(10, 12, 16, 0.78)" // User requested 0.78
              : "rgba(255, 255, 255, 0.90)" // User requested 0.90
            : "transparent",
          color:
            theme.palette.mode === "dark"
              ? "#fff"
              : scrolled
                ? "rgba(0,0,0,0.85)"
                : "#fff",
          backdropFilter: isOpaque ? "blur(8px)" : "none",
          WebkitBackdropFilter: isOpaque ? "blur(8px)" : "none",
          boxShadow: isOpaque
            ? "0 4px 18px 0px rgba(0, 0, 0, 0.08)"
            : "none !important",
          borderBottom: isOpaque
            ? `1px solid ${theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.10)"
              : "rgba(0,0,0,0.08)"
            }`
            : "1px solid transparent",
          margin: "0 !important",
          marginBottom: "0 !important",
          borderRadius: "0 !important",
          "& .MuiTypography-root, & .MuiSvgIcon-root": {
            color: "inherit",
            textShadow: "none !important",
            filter: "none !important",
            fontWeight: "600 !important",
          },
        },
      })}
      className={appBarClasses}
    >
      {settings && settings.appName ? (
        <Helmet>
          <title>{settings.appName + " | " + t("book_your_title")}</title>
          <meta name="description" content={t("about_us_content2")} />
        </Helmet>
      ) : null}

      <Toolbar
        className={classes.container}
        style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: { xs: "100%", lg: "1360px" },
          margin: "0 auto",
          px: { xs: 2, sm: 3, md: 4 },
          minHeight: "56px",
          height: "56px",
        }}
      >
        {/* Column 1 (Left): Brand Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            flex: { md: 1, xs: "none" },
            minWidth: 0,
          }}
        >
          {brandComponent}
        </Box>

        {/* Column 2 (Center): Centered Navigation Menu */}
        <Box
          component="nav"
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            flex: "0 0 auto",
          }}
        >
          {React.isValidElement(rightLinks)
            ? React.cloneElement(rightLinks, {
                isOpaque,
                scrolled,
                forceSolid,
                section: "nav",
              })
            : null}
        </Box>

        {/* Column 3 (Right): Desktop Controls (Theme Mode Toggle + Language Capsule) */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "flex-end",
            flex: 1,
            gap: 1.5,
          }}
        >
          {React.isValidElement(rightLinks)
            ? React.cloneElement(rightLinks, {
                isOpaque,
                scrolled,
                forceSolid,
                section: "actions",
              })
            : rightLinks}
        </Box>

        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: 1,
          }}
        >
          {/* Mobile language capsule (between logo and hamburger) */}
          {React.isValidElement(rightLinks)
            ? React.cloneElement(rightLinks, {
                isOpaque,
                scrolled,
                forceSolid,
                section: "mobile-lang",
              })
            : null}
          <IconButton
            aria-label={t("open_drawer", "open drawer")}
            onClick={handleDrawerToggle}
            sx={{ color: hamburgerColor, p: 0.8 }}
          >
            <MenuIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Toolbar>

      <Box component="div" sx={{ display: { md: "none", xs: "block" } }}>
        <Drawer
          variant="temporary"
          anchor={"right"}
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: { xs: "285px", sm: "320px" },
              maxWidth: "85vw",
              boxSizing: "border-box",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#12141A" : "#FFFFFF",
              color: "text.primary",
              boxShadow: "-8px 0 32px rgba(0, 0, 0, 0.25)",
              borderLeft: (theme) =>
                `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
              display: "flex",
              flexDirection: "column",
              zIndex: (theme) => theme.zIndex.drawer + 51,
            },
          }}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 50,
            "& .MuiBackdrop-root": {
              zIndex: (theme) => theme.zIndex.drawer + 50,
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {/* Drawer Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 2,
                borderBottom: (theme) =>
                  `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
              }}
            >
              <Typography
                fontFamily={FONT_FAMILY}
                sx={{
                  fontWeight: 700,
                  fontSize: "16px",
                  letterSpacing: "0.5px",
                  color: "text.primary",
                }}
              >
                Menu
              </Typography>
              <IconButton
                onClick={handleDrawerClose}
                aria-label={t("close_drawer", "close drawer")}
                size="small"
                sx={{
                  color: "text.secondary",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                  "&:hover": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Drawer Body */}
            <Box sx={{ p: 1.5, flex: 1, overflowY: "auto" }}>
              {leftLinks}
              {React.isValidElement(rightLinks)
                ? React.cloneElement(rightLinks, {
                    isOpaque,
                    scrolled,
                    forceSolid,
                    section: "drawer",
                    isDrawer: true,
                    onItemClick: handleDrawerClose,
                  })
                : rightLinks}
            </Box>
          </Box>
        </Drawer>
      </Box>
    </AppBar>
  );
}

Header.defaultProps = {
  color: "white",
  forceSolid: false,
};

Header.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "info",
    "success",
    "warning",
    "danger",
    "transparent",
    "white",
    "rose",
    "dark",
  ]),
  rightLinks: PropTypes.node,
  leftLinks: PropTypes.node,
  brand: PropTypes.string,
  fixed: PropTypes.bool,
  absolute: PropTypes.bool,
  // New prop to force opaque background (e.g. for Login page)
  forceSolid: PropTypes.bool,
  // Deprecated usage, kept loosely in propTypes to avoid crash if passed, but ignored
  changeColorOnScroll: PropTypes.object,
};
